import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Faq } from "@cabocheck/shared";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

type FaqProductType = "CaboLAB" | "CaboPOC";

type FAQItem = Pick<Faq, "question" | "response" | "product_type">;

function FAQAccordion({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  if (items.length === 0) {
    return <p className="rounded-2xl bg-gray-50 px-6 py-8 text-center text-gray-500">Aktuell keine FAQs vorhanden.</p>;
  }

  return (
    <div>
      {items.map((item, index) => (
        <div key={`${item.question}-${index}`} className="border-b border-gray-200 py-5 first:border-t sm:py-10">
          <button
            id={`faq-question-${index}`}
            className="flex w-full items-start justify-between gap-4 text-left"
            onClick={() => toggleItem(index)}
            aria-expanded={openIndex === index}
            aria-controls={`faq-answer-${index}`}
          >
            <h3 className="flex-1 text-xl font-bold md:text-3xl">{item.question}</h3>
            <svg
              aria-hidden="true"
              className={`h-8 w-8 shrink-0 transform transition-transform duration-300 ease-in-out md:h-10 md:w-10 ${
                openIndex === index ? "rotate-45" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <div
            id={`faq-answer-${index}`}
            role="region"
            aria-labelledby={`faq-question-${index}`}
            aria-hidden={openIndex !== index}
            className={`overflow-hidden text-gray-600 transition-all duration-300 ease-in-out ${
              openIndex === index ? "mt-6 max-h-96 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <p>{item.response}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FAQComponent({ items, showTypeTabs = false }: { items: FAQItem[]; showTypeTabs?: boolean }) {
  const { t } = useTranslation();

  const groupedItems = useMemo(
    () => ({
      CaboLAB: items.filter((item) => hasProductType(item.product_type, "CaboLAB")),
      CaboPOC: items.filter((item) => hasProductType(item.product_type, "CaboPOC")),
    }),
    [items],
  );

  const defaultTab: FaqProductType = groupedItems.CaboLAB.length > 0 ? "CaboLAB" : "CaboPOC";
  const visibleTabs = [
    { value: "CaboLAB" as const, label: t("CaboLAB"), items: groupedItems.CaboLAB },
    { value: "CaboPOC" as const, label: t("CaboPOC"), items: groupedItems.CaboPOC },
  ].filter((tab) => tab.items.length > 0);

  return (
    <div className="public-container py-8">
      <h1 className="my-6 text-center text-3xl sm:my-10 sm:text-5xl">Häufig gestellte Fragen</h1>

      {showTypeTabs ? (
        <Tabs defaultValue={defaultTab} className="mx-auto w-full">
          {visibleTabs.length > 1 ? (
            <TabsList className="mx-auto mb-8 grid h-auto w-full max-w-xl grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-2">
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="rounded-xl py-2.5 text-base font-semibold data-[state=active]:bg-white data-[state=active]:shadow"
                >
                  {tab.label} ({tab.items.length})
                </TabsTrigger>
              ))}
            </TabsList>
          ) : null}

          {visibleTabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="mt-0">
              <FAQAccordion items={tab.items} />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <FAQAccordion items={items} />
      )}
    </div>
  );
}

function hasProductType(productType: string | null | undefined, type: FaqProductType) {
  if (!productType) return false;
  return productType.split(",").some((t) => t.trim() === type);
}
